import React from 'react';
import { CheckCircle, AlertCircle, XCircle, Clock, Wifi, WifiOff, RefreshCw } from 'lucide-react';

export const SystemHealthIndicator = ({ health, connectionStatus }) => {
  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'degraded': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'down': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'degraded': return <AlertCircle className="w-4 h-4" />;
      case 'down': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getConnectionIcon = (status) => {
    switch (status) {
      case 'connected': return <Wifi className="w-4 h-4 text-green-600" />;
      case 'connecting':
      case 'reconnecting': return <RefreshCw className="w-4 h-4 text-yellow-600 animate-spin" />;
      default: return <WifiOff className="w-4 h-4 text-red-600" />;
    }
  };

  const getConnectionStatusText = (status) => {
    switch (status) {
      case 'connected': return 'Live';
      case 'connecting': return 'Connecting...';
      case 'reconnecting': return 'Reconnecting...';
      case 'disconnected': return 'Disconnected';
      case 'failed': return 'Connection Failed';
      default: return 'Unknown';
    }
  };

  if (!health) {
    return (
      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800">
        <Clock className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600 dark:text-gray-300">Loading...</span>
      </div>
    );
  }

  const overallStatus = health.database?.status === 'down' || health.stripe?.status === 'down' || health.api?.status === 'down' 
    ? 'down' 
    : health.database?.status === 'degraded' || health.stripe?.status === 'degraded' || health.api?.status === 'degraded'
    ? 'degraded'
    : 'healthy';

  return (
    <div className="flex items-center gap-4">
      {/* Overall System Status */}
      <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getHealthColor(overallStatus)}`}>
        {getHealthIcon(overallStatus)}
        <span className="text-sm font-medium">
          System {overallStatus.charAt(0).toUpperCase() + overallStatus.slice(1)}
        </span>
      </div>

      {/* Connection Status */}
      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800">
        {getConnectionIcon(connectionStatus)}
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {getConnectionStatusText(connectionStatus)}
        </span>
      </div>

      {/* Individual Components */}
      <div className="flex items-center gap-2">
        {Object.entries(health).map(([component, status]) => (
          <div key={component} className="group relative">
            <div className={`flex items-center gap-1 px-2 py-1 rounded ${getHealthColor(status.status)}`}>
              {getHealthIcon(status.status)}
              <span className="text-xs font-medium capitalize">{component}</span>
              {status.response_time_ms && (
                <span className="text-xs opacity-75">{status.response_time_ms}ms</span>
              )}
            </div>
            
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap">
              <div className="font-medium">{component.charAt(0).toUpperCase() + component.slice(1)}</div>
              <div>Status: {status.status}</div>
              {status.response_time_ms && <div>Response: {status.response_time_ms}ms</div>}
              {status.error_message && <div className="text-red-300">Error: {status.error_message}</div>}
              <div className="text-gray-300">
                Last check: {new Date(status.last_check).toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PerformanceMetrics = ({ metrics }) => {
  if (!metrics) return null;

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const errorRate = metrics.request_count > 0 ? (metrics.error_count / metrics.request_count * 100).toFixed(1) : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
      <div className="bg-white rounded-lg border border-gray-200 p-3 dark:bg-gray-900 dark:border-gray-700">
        <div className="text-gray-500 dark:text-gray-400">Requests</div>
        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
          {formatNumber(metrics.request_count)}
        </div>
      </div>
      
      <div className="bg-white rounded-lg border border-gray-200 p-3 dark:bg-gray-900 dark:border-gray-700">
        <div className="text-gray-500 dark:text-gray-400">Avg Response</div>
        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
          {Math.round(metrics.avg_response_time_ms)}ms
        </div>
      </div>
      
      <div className="bg-white rounded-lg border border-gray-200 p-3 dark:bg-gray-900 dark:border-gray-700">
        <div className="text-gray-500 dark:text-gray-400">Error Rate</div>
        <div className={`text-lg font-bold ${errorRate > 5 ? 'text-red-600' : errorRate > 1 ? 'text-yellow-600' : 'text-green-600'}`}>
          {errorRate}%
        </div>
      </div>
      
      <div className="bg-white rounded-lg border border-gray-200 p-3 dark:bg-gray-900 dark:border-gray-700">
        <div className="text-gray-500 dark:text-gray-400">Active Connections</div>
        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
          {metrics.active_connections}
        </div>
      </div>
    </div>
  );
};